import { useMutation } from '@apollo/client/react';
import { DISABLE_CARD_MUTATION, UPDATE_CARD_PIN_MUTATION, UPDATE_CARD_REFERENCES_MUTATION } from '../../integrations/allkort/graphql/gql/CardMutations';
import { GET_CARDS_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import {
	DisableCardInput,
	DisableCardResponse,
	UpdateCardPinCodeInput,
	UpdateCardPinCodeResponse,
	UpdateCardReferencesInput,
	UpdateCardReferencesResponse
} from '../../integrations/allkort/graphql/interface/CardMutation';

/**
 * useMutateCard
 * 
 * Custom hook for card mutation operations.
 * Currently supports:
 * - Updating card references (name, reference1, reference2)
 * - Updating card PIN code
 * - Disabling a card
 * 
 * @example
 * ```tsx
 * const { updateCardReferences, updateCardPinCode, disableCard, loading, error } = useMutateCard();
 * 
 * // Update card name
 * const result = await updateCardReferences({
 *   cardId: 1409810,
 *   reference1: "My Card Name",
 *   reference2: ""
 * });
 * 
 * // Update card PIN
 * const pinResult = await updateCardPinCode({
 *   cardId: 1409810,
 *   pinCode: 1234
 * });
 * 
 * // Disable a card
 * const disableResult = await disableCard({
 *   cardId: 1409810,
 *   reason: "By user"
 * });
 * ```
 */
export const useMutateCard = () => {
	const [updateCardReferencesMutation, { loading: refLoading, error: refError, data: refData }] = useMutation<
		UpdateCardReferencesResponse,
		{ cardId: number; reference1: string; reference2: string }
	>(UPDATE_CARD_REFERENCES_MUTATION, {
		// Refetch the cards query after successful mutation to update the cache
		refetchQueries: [
			{
				query: GET_CARDS_CUSTOMER_DATA_QUERY,
				variables: { first: null, after: null, before: null, last: 49 }
			}
		],
		// Wait for refetch to complete before returning
		awaitRefetchQueries: true
	});

	const [updateCardPinMutation, { loading: pinLoading, error: pinError, data: pinData }] = useMutation<
		UpdateCardPinCodeResponse,
		{ cardId: number; pinCode: number }
	>(UPDATE_CARD_PIN_MUTATION, {
		// Refetch the cards query after successful mutation to update the cache
		refetchQueries: [
			{
				query: GET_CARDS_CUSTOMER_DATA_QUERY,
				variables: { first: null, after: null, before: null, last: 49 }
			}
		],
		// Wait for refetch to complete before returning
		awaitRefetchQueries: true
	});

	const [disableCardMutation, { loading: disableLoading, error: disableError, data: disableData }] = useMutation<
		DisableCardResponse,
		{ cardId: number; reason: string }
	>(DISABLE_CARD_MUTATION, {
		// Refetch the cards query after successful mutation to update the cache
		refetchQueries: [
			{
				query: GET_CARDS_CUSTOMER_DATA_QUERY,
				variables: { first: null, after: null, before: null, last: 49 }
			}
		],
		// Wait for refetch to complete before returning
		awaitRefetchQueries: true
	});

	/**
	 * Update card references (name, reference fields)
	 * 
	 * @param input - Object containing cardId, reference1, and reference2
	 * @returns Promise with the mutation result
	 */
	const updateCardReferences = async (input: UpdateCardReferencesInput) => {
		return updateCardReferencesMutation({
			variables: {
				cardId: input.cardId,
				reference1: input.reference1,
				reference2: input.reference2
			}
		});
	};

	/**
	 * Update card PIN code
	 * 
	 * @param input - Object containing cardId and pinCode (4-digit PIN as integer)
	 * @returns Promise with the mutation result
	 */
	const updateCardPinCode = async (input: UpdateCardPinCodeInput) => {
		return updateCardPinMutation({
			variables: {
				cardId: input.cardId,
				pinCode: input.pinCode
			}
		});
	};

	/**
	 * Disable a card to prevent further use
	 * 
	 * @param input - Object containing cardId and reason for disabling
	 * @returns Promise with the mutation result
	 */
	const disableCard = async (input: DisableCardInput) => {
		return disableCardMutation({
			variables: {
				cardId: input.cardId,
				reason: input.reason
			}
		});
	};

	return {
		updateCardReferences,
		updateCardPinCode,
		disableCard,
		loading: refLoading || pinLoading || disableLoading,
		error: refError || pinError || disableError,
		data: refData || pinData || disableData
	};
};
